import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  Box,
  Stack,
  TextField,
  Divider
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Lead } from '../../types/Lead';
import { useUpdateLead } from '../../hooks/useLeads';
import { useClientContext } from '../../context/ClientContext';
import DeadLeadDialog from './DeadLeadDialog';
import AddClientDialog from '../Client/AddClientDialog';

interface ViewLeadDialogProps {
  lead: Lead | null;
  open: boolean;
  onClose: () => void;
  onLeadUpdated?: () => void;
}

const ViewLeadDialog: React.FC<ViewLeadDialogProps> = ({ lead, open, onClose, onLeadUpdated }) => {
  const [rescheduleDateTime, setRescheduleDateTime] = useState<Date | null>(null);
  const [rescheduleType, setRescheduleType] = useState<'callback' | 'no_answer'>('no_answer');
  const [showDeadLeadDialog, setShowDeadLeadDialog] = useState(false);
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Lead>>({});
  const { mutateAsync: updateLead } = useUpdateLead();
  const { createClient } = useClientContext();
  const navigate = useNavigate();

  const handleEdit = async () => {
    if (!lead || Object.keys(editData).length === 0) return;
    try {
      await updateLead({
        id: lead._id,
        data: editData
      });
      setIsEditing(false);
      // Stay on the page after editing
setEditData({});
    } catch (error) {
      
    }
  };

  const handleInputChange = (field: keyof Lead) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleReschedule = async () => {
    if (!lead || !rescheduleDateTime) return;

    try {
      // Create a new Date object to avoid mutating the original
      let updatedDateTime = new Date(rescheduleDateTime);
      
      // If it's not a callback, set time to 9 AM
      if (rescheduleType !== 'callback') {
        updatedDateTime.setHours(9, 0, 0, 0);
      }

      const formattedTime = updatedDateTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      const rescheduleNotes = rescheduleType === 'callback' 
        ? `Callback requested for ${formattedTime}` 
        : 'No answer - Rescheduled';
      
      // Log the data we're about to send
      /* removed console.log */

      const updatedData: Partial<Lead> = {
        status: 'RESCHEDULED' as const,
        rescheduleDateTime: updatedDateTime.toISOString(),
        isCallback: rescheduleType === 'callback',
        notes: lead.notes 
          ? `${lead.notes}\n${rescheduleNotes}` 
          : rescheduleNotes
      };

      await updateLead({
        id: lead._id,
        data: updatedData
      });
      
      // Update local state
      if (onLeadUpdated) {
        onLeadUpdated();
      }

      // Reset form state
      setRescheduleDateTime(null);
      setRescheduleType('no_answer');
      
      onClose();
    } catch (error) {
      
    }
  };

  if (!lead) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(25, 25, 25, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white', pb: 2 }}>
        <Typography variant="h5" component="div" fontWeight="600">
          Lead Details
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ color: 'white', pt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">Company Name</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.companyName || ''}
                onChange={handleInputChange('companyName')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.companyName}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">Industry</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.industry || ''}
                onChange={handleInputChange('industry')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.industry || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">Contact Name</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.contactName || ''}
                onChange={handleInputChange('contactName')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.contactName}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">City</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.city || ''}
                onChange={handleInputChange('city')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.city || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">Postcode</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.postcode || ''}
                onChange={handleInputChange('postcode')}
                inputProps={{
                  pattern: '^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$',
                  title: 'Please enter a valid UK postcode'
                }}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.postcode || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.phone || ''}
                onChange={handleInputChange('phone')}
                inputProps={{
                  pattern: '^[\\d\\s+()-]+$',
                  title: 'Please enter a valid phone number'
                }}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.phone || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">Email</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.email || ''}
                onChange={handleInputChange('email')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.email || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">Address Line 1</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.address1 || ''}
                onChange={handleInputChange('address1')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.address1 || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">Address Line 2</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.address2 || ''}
                onChange={handleInputChange('address2')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.address2 || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">Address Line 3</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={editData.address3 || ''}
                onChange={handleInputChange('address3')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.address3 || 'N/A'}</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Status</Typography>
            <Chip 
              label={lead.status} 
              color={
                lead.status === 'NEW' ? 'primary' :
                lead.status === 'CONVERTED' ? 'success' :
                lead.status === 'DEAD' ? 'error' :
                lead.status === 'RESCHEDULED' ? 'warning' :
                'default'
              }
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Created Date</Typography>
            <Typography variant="body1">
              {new Date(lead.createdAt).toLocaleDateString('en-GB', { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              })}
            </Typography>
          </Grid>
          {lead.rescheduleDateTime && (
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>Rescheduled For</Typography>
              <Box>
                <Typography variant="body1" component="span">
                  {new Date(lead.rescheduleDateTime).toLocaleDateString('en-GB', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </Typography>
                {lead.isCallback && (
                  <Chip 
                    label={new Date(lead.rescheduleDateTime).toLocaleTimeString('en-GB', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                    size="small"
                    color="info"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>Notes</Typography>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={editData.notes || ''}
                onChange={handleInputChange('notes')}
                sx={{ mt: 0.5 }}
              />
            ) : (
              <Typography variant="body1" sx={{ mt: 0.5 }}>{lead.notes || 'No notes'}</Typography>
            )}
          </Grid>
          {lead.status !== 'CONVERTED' && lead.status !== 'DEAD' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Reschedule Lead
                  </Typography>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 1.5 }}>
                        Reason
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant={rescheduleType === 'callback' ? 'contained' : 'outlined'}
                          onClick={() => setRescheduleType('callback')}
                          fullWidth
                          sx={{
                            borderColor: rescheduleType === 'callback' ? undefined : 'rgba(255, 255, 255, 0.3)',
                            color: rescheduleType === 'callback' ? undefined : 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              borderColor: 'rgba(255, 255, 255, 0.5)',
                            }
                          }}
                        >
                          Callback Requested
                        </Button>
                        <Button
                          variant={rescheduleType === 'no_answer' ? 'contained' : 'outlined'}
                          onClick={() => setRescheduleType('no_answer')}
                          fullWidth
                          sx={{
                            borderColor: rescheduleType === 'no_answer' ? undefined : 'rgba(255, 255, 255, 0.3)',
                            color: rescheduleType === 'no_answer' ? undefined : 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              borderColor: 'rgba(255, 255, 255, 0.5)',
                            }
                          }}
                        >
                          No Answer
                        </Button>
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 1.5 }}>
                        {rescheduleType === 'callback' ? 'Callback Date & Time' : 'Reschedule Date'}
                      </Typography>
                      <DateTimePicker
                        value={rescheduleDateTime}
                        onChange={(newValue) => {
                          if (rescheduleType === 'callback') {
                            setRescheduleDateTime(newValue);
                          } else {
                            if (newValue) {
                              const dateWithDefaultTime = new Date(newValue);
                              dateWithDefaultTime.setHours(9, 0, 0, 0);
                              setRescheduleDateTime(dateWithDefaultTime);
                            } else {
                              setRescheduleDateTime(null);
                            }
                          }
                        }}
                        format={rescheduleType === 'callback' ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
                        views={rescheduleType === 'callback' ? ['year', 'month', 'day', 'hours', 'minutes'] : ['year', 'month', 'day']}
                        minutesStep={15}
                        ampm={false}
                        shouldDisableTime={(value, view) => 
                          view === 'minutes' && value.getMinutes() % 15 !== 0
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            placeholder: rescheduleType === 'callback' ? 'Select date and time' : 'Select date',
                            sx: {
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              borderRadius: 1,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                '& fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.87)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#1976d2',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(0, 0, 0, 0.6)',
                              },
                              '& .MuiOutlinedInput-input': {
                                color: 'rgba(0, 0, 0, 0.87)',
                              },
                            }
                          },
                          popper: {
                            sx: {
                              '& .MuiPaper-root': {
                                backgroundColor: 'rgba(30, 30, 30, 0.98)',
                                backdropFilter: 'blur(12px)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: 'white',
                              },
                              '& .MuiPickersCalendarHeader-root': {
                                color: 'white',
                              },
                              '& .MuiPickersCalendarHeader-label': {
                                color: 'white',
                              },
                              '& .MuiIconButton-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                              },
                              '& .MuiDayCalendar-weekDayLabel': {
                                color: 'rgba(255, 255, 255, 0.6)',
                              },
                              '& .MuiPickersDay-root': {
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: '#1565c0',
                                  },
                                },
                                '&.Mui-disabled': {
                                  color: 'rgba(255, 255, 255, 0.3)',
                                },
                              },
                              '& .MuiPickersDay-today': {
                                border: '1px solid rgba(255, 255, 255, 0.5)',
                              },
                              '& .MuiClock-root': {
                                backgroundColor: 'transparent',
                              },
                              '& .MuiClockNumber-root': {
                                color: 'white',
                              },
                              '& .MuiClock-pin': {
                                backgroundColor: '#1976d2',
                              },
                              '& .MuiClockPointer-root': {
                                backgroundColor: '#1976d2',
                              },
                              '& .MuiClockPointer-thumb': {
                                backgroundColor: '#1976d2',
                                borderColor: '#1976d2',
                              },
                              '& .MuiPickersYear-yearButton': {
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: '#1565c0',
                                  },
                                },
                              },
                              '& .MuiDialogActions-root': {
                                '& .MuiButton-root': {
                                  color: '#1976d2',
                                },
                              },
                            }
                          }
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Stack direction="row" spacing={2}>
          {isEditing ? (
            <>
              <Button 
                onClick={() => {
                  setIsEditing(false);
                  setEditData({});
                }}
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleEdit} 
                variant="contained" 
                color="primary"
                disabled={Object.keys(editData).length === 0}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={onClose}
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                Close
              </Button>
              {(lead.status === 'NEW' || lead.status === 'RESCHEDULED') && (
                <>
                  <Button 
                    onClick={() => setShowAddClientDialog(true)} 
                    variant="contained" 
                    color="primary"
                    sx={{
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      backdropFilter: 'blur(4px)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    Convert to Client
                  </Button>
                  <Button 
                    onClick={() => setShowDeadLeadDialog(true)} 
                    variant="contained" 
                    color="error"
                    sx={{
                      backgroundColor: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.dark',
                      },
                      backdropFilter: 'blur(4px)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    Mark as Dead Lead
                  </Button>
                </>
              )}
              {lead.status !== 'CONVERTED' && lead.status !== 'DEAD' && rescheduleDateTime && (
                <Button 
                  onClick={handleReschedule} 
                  variant="contained" 
                  color="warning"
                  sx={{
                    backgroundColor: 'warning.main',
                    '&:hover': {
                      backgroundColor: 'warning.dark',
                    },
                    backdropFilter: 'blur(4px)',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Reschedule
                </Button>
              )}
            </>
          )}
        </Stack>
      </DialogActions>

      {/* Dead Lead Dialog */}
      <DeadLeadDialog
        open={showDeadLeadDialog}
        onClose={() => setShowDeadLeadDialog(false)}
        leadId={lead._id}
        onLeadMarkedAsDead={() => {
          // Update local state to reflect the dead status
          setEditData(prev => ({ ...prev, status: 'DEAD' }));
          // Trigger a refresh of the leads data
          if (onLeadUpdated) {
            onLeadUpdated();
          }
          onClose();
        }}
      />

      {/* Add Client Dialog */}
      <AddClientDialog
        open={showAddClientDialog}
        onClose={() => setShowAddClientDialog(false)}
        onSubmit={async (clientData) => {
          try {
            // First create the client and get its ID
            const newClient = await createClient(clientData);
            
            // Then update the lead status
            await updateLead({
              id: lead._id,
              data: { status: 'CONVERTED' }
            });
            
            setShowAddClientDialog(false);
            onClose();
            navigate(`/utilities/clients/${newClient._id}`);
          } catch (error) {
            
          }
        }}
        initialData={{
          companyName: lead.companyName,
          ownerName: lead.contactName,
          phoneNumber: lead.phone,
          email: lead.email,
          industry: lead.industry || '',
          notes: lead.notes || '',
          tradingAddress: {
            line1: lead.address1 || '',
            line2: lead.address2 || '',
            city: lead.city || '',
            county: lead.address3 || '',
            postcode: lead.postcode || '',
            country: 'United Kingdom'
          }
        }}
      />
    </Dialog>
  );
};

export default ViewLeadDialog;
